import React from 'react'
import { useController, Control, FieldErrors } from 'react-hook-form'
import { CustomFieldDefinition } from '@/actors/AuthActor/types'
import { useTranslation } from '@/localization/useTranslation'
import { TextInput } from '@/components/Inputs'
// Straight from its own module, not the barrel — see PersonalInfo for why.
import PhoneInput from '@/components/Inputs/PhoneInput'
import { useGeolocation } from '@/hooks/geolocation/useGeolocation'
import { isValidPhone } from '@/utils/phone'

type RegisterFormValues = {
  username: string
  password: string
  firstName: string
  lastName: string
  confirmEmail: string
  termsAndConditions: boolean
  [key: string]: unknown
}

interface CustomFieldProps {
  field: CustomFieldDefinition
  control: Control<RegisterFormValues>
  errors: FieldErrors<RegisterFormValues>
}

const CustomField: React.FC<CustomFieldProps> = ({ field, control, errors }) => {
  const { t } = useTranslation()
  const { isAllowed, isLoading, country: geoCountry } = useGeolocation({
    allow: field.geolocation?.allow || [],
    deny: field.geolocation?.deny || []
  })

  const isPhone = field.type === 'phone'
  const validationMessage = field.validation?.message ? t(field.validation.message) : ''
  // The library owns what a valid number looks like, so a `phone` field needs no
  // configured message to fall back on the way a regex-driven one does.
  const phoneValidationMessage = validationMessage || t('Invalid mobile number')
  // A rule with no message blocks the submit and explains nothing, and the
  // documented `phone` config carries no `validation` block at all.
  const requiredMessage = validationMessage || t('This field is required')

  const stripPrefix = (value: unknown) =>
    field.prefix && typeof value === 'string' ? value.replace(field.prefix, '') : value

  const isFieldActive = !field.geolocation || isAllowed || isLoading

  // An unresolved country leaves the field optional: a geo outage must not
  // block a signup (FZSPF-3792).
  // An emptied list falls back to `required` rather than silently dropping it.
  const isRequired = field.requiredIn?.length
    ? !!geoCountry && field.requiredIn.includes(geoCountry)
    : !!field.required

  const { field: controllerField } = useController({
    name: field.name as keyof RegisterFormValues & string,
    control,
    shouldUnregister: true,
    disabled: !isFieldActive,
    rules: {
      required: isFieldActive && isRequired
        ? { value: true, message: requiredMessage }
        : false,
      pattern: !isPhone && isFieldActive && field.validation?.pattern && !field.prefix
        ? { value: new RegExp(field.validation.pattern), message: validationMessage }
        : undefined,
      // Length rules are written for a national number; a phone field holds the
      // full E.164, so a `maxLength: 9` meant for "9 digits" would reject every
      // valid +51… the brand configured. The schema already promises `phone`
      // ignores these — only `pattern` was honouring it.
      minLength: !isPhone && isFieldActive && field.validation?.minLength
        ? { value: field.validation.minLength, message: validationMessage }
        : undefined,
      maxLength: !isPhone && isFieldActive && field.validation?.maxLength
        ? { value: field.validation.maxLength, message: validationMessage }
        : undefined,
      validate: isPhone && isFieldActive
        ? (value) => !value || isValidPhone(value as string) || phoneValidationMessage
        : isFieldActive && field.validation?.pattern
          ? (value) => {
            const bare = stripPrefix(value) as string
            return new RegExp(field.validation!.pattern!).test(bare) || validationMessage
          }
          : undefined,
    },
  })

  if (field.geolocation && !isAllowed && !isLoading) return null

  if (isPhone) {
    return (
      <PhoneInput
        errors={errors}
        label={t(field.label)}
        required={isRequired}
        // RHF's `field.ref` is deliberately NOT forwarded. Reading `.ref` during
        // render taints the whole controller object for the React Compiler, and
        // `react-hooks/refs` is an unconditional error here — it flags every
        // later `controllerField.*` read in the same render. The cost is that an
        // invalid submit cannot auto-focus this one field; the error still shows.
        name={controllerField.name}
        value={controllerField.value as string}
        onChange={controllerField.onChange}
        onBlur={controllerField.onBlur}
        disabled={controllerField.disabled}
        countries={field.countries}
        defaultCountry={field.defaultCountry ?? geoCountry}
      />
    )
  }

  const displayValue = field.prefix && typeof controllerField.value === 'string'
    ? controllerField.value.replace(field.prefix, '')
    : controllerField.value as string ?? ''

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    controllerField.onChange(field.prefix ? `${field.prefix}${raw}` : raw)
  }

  return (
    <TextInput
      errors={errors}
      label={t(field.label)}
      type={field.type}
      required={isRequired}
      leftIcon={field.prefix ? <span>{field.prefix}</span> : undefined}
      {...controllerField}
      value={displayValue}
      onChange={handleChange}
    />
  )
}

export default CustomField
